import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function CambridgeshireCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Cambridgeshire Pothole Claims | 277,000 Repairs in Five Years | Fixtyer"
        description="Cambridgeshire filled 277,406 potholes in five years yet admits its network has deteriorated — 40% of roads sit on peat soils and 56% of 2024/25 maintenance was reactive. See what the DfT AMBER rating means for your claim."
        canonical="/councils/cambridgeshire"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Cambridgeshire', url: '/councils/cambridgeshire' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Cambridgeshire has GREEN spend — can I still claim for pothole damage?',
            answer: 'Yes. The DfT Spend scorecard is GREEN because Cambridgeshire invests well beyond its DfT capital allocation — £59.3m projected capital spend in 2025/26 against £29.8m allocated. But the overall rating is AMBER because road condition is AMBER, and the council itself states the network has deteriorated over the last five years. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on aggregate spend.',
          },
          {
            question: 'What if my pothole was on a peat or soil-affected road?',
            answer: 'Around 40% of Cambridgeshire\'s road network lies on peat-based soils that deteriorate faster than conventional roads — 54% of unclassified roads and 93% of Fenland\'s network are soil affected. The council admits surface treatments are often ineffective on these routes and maintenance costs £30,000 per kilometre versus £25,000 elsewhere. That is documented knowledge of elevated deterioration risk, which matters for what a reasonable inspection regime looks like under Section 58.',
          },
          {
            question: 'Does the jump in RED B/C roads after 2023 mean my claim is stronger?',
            answer: 'Be careful with the headline numbers. B/C roads in RED jumped from 6.55% (2022, SCANNER) to 14.29% (2023, Gaist imagery). Cambridgeshire\'s own report states this "is not indicative of a rapid deterioration of the network, rather a difference in the data collection method." The underlying decline admission and peat-soil pressures are stronger arguments than the methodology-disrupted percentages alone.',
          },
          {
            question: 'What if my pothole was on an unclassified road?',
            answer: 'U-roads make up 2,296km — roughly 51% of Cambridgeshire\'s 4,519km road network. Under the older Coarse Visual Inspection (CVI) survey, 35-36% of U-roads were in RED condition every year from 2020 to 2022. After switching to Gaist processed imagery in 2023/24, RED U-roads were reported at 20.88% and 22.36% — figures the council warns are not directly comparable to the CVI years.',
          },
          {
            question: 'Cambridgeshire repaired 52,229 potholes in 2024/25 — does that mean the roads are fixed?',
            answer: 'No. The council repaired an average of 55,400 potholes every year over five years — 277,406 in total — while simultaneously admitting overall network condition has deteriorated. In 2024/25, 56% of maintenance spend was reactive and only 22% was preventative. A network producing potholes at this rate is one where defects routinely form between inspections.',
          },
          {
            question: 'Does Cambridgeshire prioritise preventative work over roads in poor condition?',
            answer: 'The council says so explicitly: "we may have to prioritise roads that need preventative treatment over roads in a poorer condition also requiring attention." That is an admission that some roads in poor condition may not receive timely maintenance — directly relevant if your pothole sat on a deteriorating route that was deprioritised for preventative work elsewhere.',
          },
        ]}
      />
      <ServiceSchema 
        councilName="Cambridgeshire County Council" 
        councilSlug="cambridgeshire" 
        rating="amber" 
      />
      <SpeakableSchema
        url="/councils/cambridgeshire"
        headline="Cambridgeshire Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div 
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Cambridgeshire</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>
          
          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Cambridgeshire: Record Spending, Peat Roads, Declining Network
          </h1>
          
          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Cambridgeshire is investing £73 million in 2025/26 — the largest highways spend in a decade — 
            and earns a <strong className="text-emerald-400">GREEN</strong> DfT spend scorecard. Yet the overall 
            rating is <strong className="text-amber-400">AMBER</strong> because the council admits its network 
            has deteriorated over five years, <strong className="text-rose-400">40% of roads sit on peat soils</strong> that 
            fail faster than conventional surfaces, and it filled <strong className="text-rose-400">277,406 potholes in five years</strong> while 
            56% of 2024/25 maintenance was reactive.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">277,406</div>
            <div className="text-white font-medium mt-2">Potholes filled in five years</div>
            <div className="text-brand-300 text-sm mt-1">
              An average of 55,400 repairs every year across Cambridgeshire's 4,519km network — 
              including a peak of 65,219 in 2023/24 — while the council acknowledges overall 
              condition has still deteriorated.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of survey data from Cambridgeshire's own transparency report — 
            stable under SCANNER, disrupted by a 2023 methodology switch to Gaist imagery
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard 
              quote="Often for reasons outside our control, such as changing climate and rising demand, the overall condition of our road network has deteriorated over the last five years. This is why we are investing more of the council's reserve funds in highways maintenance and lobbying central government for further funding."
              source="Cambridgeshire County Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (517km — 11.4% of network): SCANNER era vs Gaist era</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                    <th className="px-4 py-2 text-center font-semibold text-surface-500">Method</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">2.54%</td>
                    <td className="px-4 py-2 text-center">17.48%</td>
                    <td className="px-4 py-2 text-center">79.99%</td>
                    <td className="px-4 py-2 text-center text-xs text-surface-500">SCANNER</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">2.53%</td>
                    <td className="px-4 py-2 text-center">17.86%</td>
                    <td className="px-4 py-2 text-center">79.60%</td>
                    <td className="px-4 py-2 text-center text-xs text-surface-500">SCANNER</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">3.18%</td>
                    <td className="px-4 py-2 text-center">21.19%</td>
                    <td className="px-4 py-2 text-center">75.63%</td>
                    <td className="px-4 py-2 text-center text-xs text-surface-500">SCANNER</td>
                  </tr>
                  <tr className="border-b border-surface-100 bg-amber-50">
                    <td className="px-4 py-2 font-mono">2023*</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">6.26%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">53.46%</td>
                    <td className="px-4 py-2 text-center font-bold">39.74%</td>
                    <td className="px-4 py-2 text-center text-xs text-amber-700">Gaist</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024*</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">7.09%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">57.78%</td>
                    <td className="px-4 py-2 text-center font-bold">35.13%</td>
                    <td className="px-4 py-2 text-center text-xs text-amber-700">Gaist</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              Under comparable SCANNER data (2020–2022), A-road RED condition rose modestly from 2.54% to 3.18%. 
              The 2023/24 jump reflects a survey methodology change, not necessarily overnight collapse — 
              though the council's own deterioration admission stands independently of the chart.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (1,706km — 37.8% of network): stable then disrupted</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020 (Scanner)</td>
                    <td className="px-4 py-2 text-center">6.96%</td>
                    <td className="px-4 py-2 text-center">27.29%</td>
                    <td className="px-4 py-2 text-center">65.74%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021 (Scanner)</td>
                    <td className="px-4 py-2 text-center">6.79%</td>
                    <td className="px-4 py-2 text-center">26.86%</td>
                    <td className="px-4 py-2 text-center">66.35%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022 (Scanner)</td>
                    <td className="px-4 py-2 text-center">6.55%</td>
                    <td className="px-4 py-2 text-center">27.39%</td>
                    <td className="px-4 py-2 text-center">66.05%</td>
                  </tr>
                  <tr className="border-b border-amber-100 bg-rose-50">
                    <td className="px-4 py-2 font-mono">2023* (Gaist)</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">14.29%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">67.04%</td>
                    <td className="px-4 py-2 text-center font-bold">18.46%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024* (Gaist)</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">15.76%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">67.65%</td>
                    <td className="px-4 py-2 text-center font-bold">18.09%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              Under SCANNER, B/C RED roads held steady at 6.5–7%. After the Gaist switch, RED more than doubled 
              to 15.76% and green-condition roads fell from 66% to 18%. The council attributes this to 
              "a difference in the data collection method" — but 83% of B/C roads now sit in RED or AMBER 
              under the current survey regime.
            </p>
          </div>

          {/* Methodology caveat */}
          <div className="bg-brand-950 text-white rounded-2xl p-8 mb-6">
            <h3 className="font-display text-xl font-bold mb-4">The Gaist Methodology Asterisk</h3>
            <QuoteCard 
              quote="The reporting parameters are different, hence there are differences in the reported figures as outlined below. This is not indicative of a rapid deterioration of the network, rather a difference in the data collection method."
              source="Cambridgeshire County Council Local Highways Maintenance Transparency Report 2025"
            />
            <p className="text-brand-200 mt-6 text-sm">
              Cambridgeshire switched from SCANNER machine data (2020–2022) to Gaist processed imagery (2023–2024) 
              for A, B and C roads, and from Coarse Visual Inspection to Gaist for U-roads. From 2025, it uses 
              SCANNER for the full network again ahead of PAS 2161. Cross-era percentage comparisons need caution — 
              but the council's explicit five-year deterioration admission and 277,406 pothole repairs do not 
              depend on any single survey year's percentages.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold text-emerald-900 mb-4">GREEN Spend — But Mostly Reactive in 2024/25</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-600">£29.8m</div>
                <div className="text-emerald-800 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-600">£59.3m</div>
                <div className="text-emerald-800 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-rose-600">56%</div>
                <div className="text-emerald-800 text-sm">Reactive maintenance share in 2024/25</div>
              </div>
            </div>
            <p className="text-emerald-900 font-medium">
              Cambridgeshire spends nearly <span className="text-emerald-700">double its DfT allocation</span> — 
              yet in 2024/25 only 22% of maintenance was preventative and 56% was reactive. 
              The GREEN spend rating reflects investment volume, not whether that investment is 
              keeping pace with a peat-affected network producing 55,000+ potholes a year.
            </p>
          </div>
        </div>
      </section>

      {/* The Peat Problem */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 40% Peat Problem
          </h2>
          <p className="text-surface-600 mb-8">
            Cambridgeshire's unique geology — and why it matters for your claim
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard 
              quote="Around 40% of Cambridgeshire's entire road network lies on peat-based soils. These soils naturally saturate and expand in wet weather and dry out and contract in dry weather. Climate change and unpredictable weather patterns can have a significant impact on these routes, resulting in excessive and accelerated deformation."
              source="Cambridgeshire County Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Network (km)</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Soil affected (km)</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">% of class affected</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">A</td>
                  <td className="px-6 py-4 text-center">480</td>
                  <td className="px-6 py-4 text-center">147</td>
                  <td className="px-6 py-4 text-center">31%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">B</td>
                  <td className="px-6 py-4 text-center">578</td>
                  <td className="px-6 py-4 text-center">251</td>
                  <td className="px-6 py-4 text-center">44%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">C</td>
                  <td className="px-6 py-4 text-center">1,121</td>
                  <td className="px-6 py-4 text-center">358</td>
                  <td className="px-6 py-4 text-center">32%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">U</td>
                  <td className="px-6 py-4 text-center font-bold">2,244</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">906</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">54%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Total</td>
                  <td className="px-6 py-4 text-center font-bold">4,423</td>
                  <td className="px-6 py-4 text-center font-bold">1,662</td>
                  <td className="px-6 py-4 text-center font-bold">38%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">£5,000 More Per Kilometre</h3>
              <p className="text-surface-600 text-sm mb-3">
                Soil-affected roads cost <strong>£30,000 per kilometre</strong> to maintain versus 
                <strong> £25,000</strong> for non-soil-affected locations. Surface treatments are 
                "often ineffective" on peat routes — the council's words — meaning potholes recur 
                faster and full reconstruction is more frequently required.
              </p>
              <p className="text-surface-600 text-sm">
                Fenland is <strong>93% soil affected</strong>. East Cambridgeshire is 28%. 
                Cambridge and South Cambridgeshire report 0% — but your claim's geography determines 
                which maintenance regime applied to your road.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Funding Squeeze Admission</h3>
              <QuoteCard 
                quote="As a result, a greater proportion of our funding goes on maintaining these routes, and this in turn impacts how much we can spend across the rest of our network. Especially as the interventions required are more costly due to surface treatments not being effective owing to the profile of the road."
                source="Cambridgeshire County Council Local Highways Maintenance Transparency Report 2025"
              />
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              Cambridgeshire documents that peat roads deteriorate faster, cost more, and consume 
              disproportionate budget. Some routes already carry warning signs or lane closures under 
              a "risk-assessed approach." For your claim, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road on peat soil — and did the council apply enhanced inspection frequency?</li>
              <li>• If surface treatments don't work on peat, why was your pothole patched rather than reconstructed?</li>
              <li>• Did budget pressure on soil-affected routes mean your road was deprioritised?</li>
              <li>• Was there prior warning signage or traffic management on your route — proof of known risk?</li>
            </ul>
            <p className="text-white font-medium">
              Documented knowledge of accelerated deterioration on peat roads raises the standard 
              for what a "reasonable" maintenance system looks like on affected routes.
            </p>
          </div>
        </div>
      </section>

      {/* U-road condition */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 2,296km Unclassified Network
          </h2>
          <p className="text-surface-600 mb-8">
            51% of Cambridgeshire's roads — disproportionately peat-affected and survey-disrupted
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Survey method</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">35%</td>
                  <td className="px-6 py-4 text-center text-surface-500">CVI</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">36%</td>
                  <td className="px-6 py-4 text-center text-surface-500">CVI</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">35%</td>
                  <td className="px-6 py-4 text-center text-surface-500">CVI</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2023*</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">20.88%</td>
                  <td className="px-6 py-4 text-center text-amber-700">Gaist</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024*</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">22.36%</td>
                  <td className="px-6 py-4 text-center text-amber-700">Gaist</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">One in Three Under CVI</h3>
              <p className="text-surface-600 text-sm mb-3">
                Under the comparable Coarse Visual Inspection surveys (2020–2022), 
                <strong> 35–36% of unclassified roads were in RED condition</strong> every single year — 
                roughly 800km of residential streets, village routes and rural lanes.
              </p>
              <p className="text-surface-600 text-sm">
                54% of U-roads are soil affected. The council collects 100% U-road coverage annually 
                in one lane only — but methodology changes mean the 2023/24 Gaist figures cannot be 
                directly compared to the CVI era.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Prioritisation Admission</h3>
              <QuoteCard 
                quote="This means we may have to prioritise roads that need preventative treatment over roads in a poorer condition also requiring attention."
                source="Cambridgeshire County Council Local Highways Maintenance Transparency Report 2025"
              />
            </div>
          </div>
        </div>
      </section>

      {/* The Repair Conveyor Belt */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            277,406 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            The scale of reactive repair tells you how many potholes this network produces
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Estimated cost</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Reactive share</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">61,532</td>
                  <td className="px-6 py-4 text-center">£2,721,235</td>
                  <td className="px-6 py-4 text-center">30%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">47,755</td>
                  <td className="px-6 py-4 text-center">£2,000,081</td>
                  <td className="px-6 py-4 text-center">35%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">50,671</td>
                  <td className="px-6 py-4 text-center">£2,619,686</td>
                  <td className="px-6 py-4 text-center">43%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">65,219</td>
                  <td className="px-6 py-4 text-center font-bold">£3,690,370</td>
                  <td className="px-6 py-4 text-center">43%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">52,229</td>
                  <td className="px-6 py-4 text-center">£4,556,273</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">56%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">277,406</td>
                  <td className="px-6 py-4 text-center font-bold">£15,587,645</td>
                  <td className="px-6 py-4 text-center text-surface-400">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~152 Potholes a Day, Every Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Cambridgeshire fills around 152 potholes per day. 
                The council estimates 55,000 repairs in 2025/26 alone. A network producing 
                defects at this rate is one where potholes routinely form between inspections — 
                exactly the scenario where prior reports and photographic evidence decide claims.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Reactive Dominance in 2024/25</h3>
              <p className="text-amber-800 text-sm">
                Despite record capital investment, 2024/25 saw reactive maintenance rise to 
                <strong> 56%</strong> of total spend — with preventative work falling to just 
                <strong> 22%</strong>. Pothole repair costs also rose to £4.56 million in 2024/25, 
                the highest in the five-year period, even with fewer individual fills than 2023/24.
              </p>
            </div>
          </div>

          {/* Spending table */}
          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden">
            <h3 className="font-semibold text-brand-900 px-6 py-4 border-b border-surface-200">
              Five-Year Spending Overview
            </h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-2 text-center font-semibold text-brand-900">DfT allocation</th>
                  <th className="px-4 py-2 text-center font-semibold text-brand-900">Total capital</th>
                  <th className="px-4 py-2 text-center font-semibold text-brand-900">Revenue</th>
                  <th className="px-4 py-2 text-center font-semibold text-emerald-700">Preventative</th>
                  <th className="px-4 py-2 text-center font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-2 font-mono">2025/26 (proj.)</td>
                  <td className="px-4 py-2 text-center">£29.8m</td>
                  <td className="px-4 py-2 text-center font-bold">£59.3m</td>
                  <td className="px-4 py-2 text-center">£14.5m</td>
                  <td className="px-4 py-2 text-center">29%</td>
                  <td className="px-4 py-2 text-center">41%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-emerald-50">
                  <td className="px-4 py-2 font-mono">2024/25</td>
                  <td className="px-4 py-2 text-center">£21.0m</td>
                  <td className="px-4 py-2 text-center font-bold text-emerald-700">£49.4m</td>
                  <td className="px-4 py-2 text-center">£8.2m</td>
                  <td className="px-4 py-2 text-center">22%</td>
                  <td className="px-4 py-2 text-center font-bold text-rose-600">56%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-2 font-mono">2023/24</td>
                  <td className="px-4 py-2 text-center">£24.3m</td>
                  <td className="px-4 py-2 text-center">£23.8m</td>
                  <td className="px-4 py-2 text-center">£10.3m</td>
                  <td className="px-4 py-2 text-center">50%</td>
                  <td className="px-4 py-2 text-center">43%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-2 font-mono">2022/23</td>
                  <td className="px-4 py-2 text-center">£18.6m</td>
                  <td className="px-4 py-2 text-center">£26.1m</td>
                  <td className="px-4 py-2 text-center">£10.9m</td>
                  <td className="px-4 py-2 text-center">44%</td>
                  <td className="px-4 py-2 text-center">43%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-2 font-mono">2021/22</td>
                  <td className="px-4 py-2 text-center">£18.7m</td>
                  <td className="px-4 py-2 text-center">£29.3m</td>
                  <td className="px-4 py-2 text-center">£8.8m</td>
                  <td className="px-4 py-2 text-center">53%</td>
                  <td className="px-4 py-2 text-center">35%</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 font-mono">2020/21</td>
                  <td className="px-4 py-2 text-center">£24.8m</td>
                  <td className="px-4 py-2 text-center">£25.2m</td>
                  <td className="px-4 py-2 text-center">£11.4m</td>
                  <td className="px-4 py-2 text-center">48%</td>
                  <td className="px-4 py-2 text-center">30%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Council's own response */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Council's Own Assessment
          </h2>
          <p className="text-surface-600 mb-8">
            What Cambridgeshire says about its DfT ratings — and what it admits still needs fixing
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard 
              quote="Improving the state of our network is a top priority. We are pleased to see our record investment in highways recognised with a green score, but after decades of underinvestment we know there is more to do. We're pushing hard to update our old processes and make sure we're delivering the best possible service we can and hope to see that progress reflected in the next scorecard."
              source="Cllr Alex Beckett, Chair of Highways and Transport Committee — Cambridgeshire County Council, January 2026"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">AMBER Best Practice</h3>
              <p className="text-surface-600 text-sm mb-3">
                Cambridgeshire scores AMBER on best practice despite pioneering data-led capital 
                prioritisation and an Active Travel Hierarchy. The council is reviewing its Highway 
                Operational Standards and transitioning to PAS 2161 from 2026/27 — evidence of 
                systems still in flux rather than fully mature.
              </p>
              <p className="text-surface-600 text-sm">
                In 2024/25 the council resurfaced 145km of road and applied preventative surface 
                dressing to 92km — but simultaneously admitted overall condition has deteriorated 
                over five years.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">£530 Million Peat Gap</h3>
              <p className="text-amber-800 text-sm mb-3">
                The council estimates soil-affected roads will require in excess of 
                <strong> £530 million</strong> in additional funding — and is lobbying central 
                government for a fairer model. In 2024/25 alone, 11km of peat-affected roads were 
                reconstructed at a cost of £5.5 million.
              </p>
              <p className="text-amber-800 text-sm">
                That funding gap is an admission the current programme cannot keep pace with 
                deterioration on peat routes — relevant when assessing whether your specific 
                defect should have been caught sooner.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Funded AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Cambridgeshire invests heavily — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — £59.3m projected capital spend vs £29.8m DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Record £73m total investment in 2025/26 including £24m from council reserves
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 100% annual road condition survey coverage on A, B, C and U roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Data-led capital prioritisation and documented asset management approach
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 145km resurfaced in 2024/25 with 92km of preventative surface dressing
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a well-documented Section 58 defence on recently resurfaced A-roads. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — council admits five-year network deterioration
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 40% peat-affected network with documented accelerated deformation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 35–36% of U-roads in RED under comparable CVI surveys (2020–2022)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 277,406 potholes filled in five years — defects form faster than repairs stick on peat
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 56% reactive maintenance in 2024/25 despite record capital investment
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Explicit admission of prioritising preventative work over roads already in poor condition
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> £530m funding gap on soil-affected roads — maintenance programme cannot keep pace
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and record investment, your claim lives or dies on 
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• Whether your road is peat-affected — Fenland (93%), East Cambridgeshire (28%), or elsewhere</li>
              <li>• Road class — on a U-road, the 35% CVI RED rate and 54% soil-affected share are structural arguments</li>
              <li>• Whether warning signs or lane closures were already in place — proof of known risk</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and 
              cites Cambridgeshire's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Cambridgeshire?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No peat-soil deterioration argument</li>
                <li>• No prior-report search</li>
                <li>• No survey methodology analysis</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ Five-year deterioration admission cited</li>
                <li>✅ Peat-soil and Fenland arguments tailored</li>
                <li>✅ 277,406 potholes in five years documented</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Cambridgeshire</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=cambridgeshire"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Cambridgeshire Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Cambridgeshire has GREEN spend — can I still claim for pothole damage?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN because Cambridgeshire invests well beyond its DfT 
                capital allocation — £59.3m projected capital spend in 2025/26 against £29.8m allocated. 
                But the overall rating is AMBER because road condition is AMBER, and the council itself 
                states the network has deteriorated over the last five years. Section 58 turns on whether 
                the specific defect that damaged your vehicle was reasonably inspected and repaired, 
                not on aggregate spend.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a peat or soil-affected road?
              </h3>
              <p className="text-surface-600 text-sm">
                Around 40% of Cambridgeshire's road network lies on peat-based soils that deteriorate 
                faster than conventional roads — 54% of unclassified roads and 93% of Fenland's network 
                are soil affected. The council admits surface treatments are often ineffective on these 
                routes and maintenance costs £30,000 per kilometre versus £25,000 elsewhere. That is 
                documented knowledge of elevated deterioration risk, which matters for what a reasonable 
                inspection regime looks like under Section 58.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the jump in RED B/C roads after 2023 mean my claim is stronger?
              </h3>
              <p className="text-surface-600 text-sm">
                Be careful with the headline numbers. B/C roads in RED jumped from 6.55% (2022, SCANNER) 
                to 14.29% (2023, Gaist imagery). Cambridgeshire's own report states this "is not indicative 
                of a rapid deterioration of the network, rather a difference in the data collection method." 
                The underlying decline admission and peat-soil pressures are stronger arguments than the 
                methodology-disrupted percentages alone.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on an unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 2,296km — roughly 51% of Cambridgeshire's 4,519km road network. Under 
                the older Coarse Visual Inspection (CVI) survey, 35-36% of U-roads were in RED condition 
                every year from 2020 to 2022. After switching to Gaist processed imagery in 2023/24, 
                RED U-roads were reported at 20.88% and 22.36% — figures the council warns are not 
                directly comparable to the CVI years.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Cambridgeshire repaired 52,229 potholes in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. The council repaired an average of 55,400 potholes every year over five years — 
                277,406 in total — while simultaneously admitting overall network condition has deteriorated. 
                In 2024/25, 56% of maintenance spend was reactive and only 22% was preventative. A network 
                producing potholes at this rate is one where defects routinely form between inspections.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Cambridgeshire prioritise preventative work over roads in poor condition?
              </h3>
              <p className="text-surface-600 text-sm">
                The council says so explicitly: "we may have to prioritise roads that need preventative 
                treatment over roads in a poorer condition also requiring attention." That is an admission 
                that some roads in poor condition may not receive timely maintenance — directly relevant 
                if your pothole sat on a deteriorating route that was deprioritised for preventative work 
                elsewhere.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources: <a 
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a> | <a 
              href="https://www.cambridgeshire.gov.uk/residents/travel-roads-and-parking/transport-plans-and-policies/how-we-are-improving-highways-maintenance"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Cambridgeshire County Council Local Highways Maintenance Transparency Report 2025 (published 30 June 2025)
            </a>. 
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
